<template>
  <!-- No @close: the backdrop and Escape leave it open, so the two buttons are the only way out. -->
  <Dialog
    v-if="modalOpen"
    headless
    :title="t('The reservation time has expired')"
    class="w-full max-w-120 md:min-w-0!"
    body-class="p-6 flex flex-col gap-4 text-foreground">
    <h2 class="flex items-center gap-2 text-xl font-semibold">
      <span
        class="flex size-7 flex-none items-center justify-center rounded-full bg-error-muted text-error-muted-foreground">
        <LockIcon class="size-4" aria-hidden="true" />
      </span>
      {{ t("The reservation time has expired") }}
    </h2>
    <p class="text-muted-foreground leading-relaxed">
      {{
        t(
          "Check ticket availability and reserve again for 10 minutes, or release the tickets and return to the homepage to start fresh."
        )
      }}
    </p>
    <div class="flex flex-col gap-2 mt-2">
      <Button class="w-full min-h-11 flex items-center justify-center gap-2" :disabled="busy" @click="reserveAgain">
        <LockIcon class="size-4" aria-hidden="true" />
        {{ t("Check availability & reserve again") }}
      </Button>
      <Button
        variant="outlined"
        color="neutral"
        class="w-full min-h-11 flex items-center justify-center"
        :disabled="busy"
        @click="goHome">
        {{ t("Go back to homepage") }}
      </Button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, h, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { isNavigationFailure, useRoute, useRouter } from "vue-router";
import { Button, Dialog } from "@xs2event/js-lib/components";
import { lockIcon as LockIcon } from "@xs2event/js-lib/icons";
import { useToast } from "@xs2event/js-lib/toast";
import brandconfig, { isB2C } from "@/brandconfig";
import { useCartStore } from "@/store/cart.ts";

/**
 * What happens when the ticket reservation runs out while there are still tickets in the cart.
 *
 * In the checkout a modal that only its own two buttons close: reserve again, or release the tickets
 * and go home. Anywhere else a toast for a minute with a button to reserve again.
 *
 * Mounted once in App.vue rather than inside ReservationTimer: the timer only renders in the checkout
 * header and the open cart drawer, and the expiry has to be noticed on every page.
 */

const CHECKOUT_ROUTES = ["checkout", "checkout-booking-details", "checkout-order-confirmation"];
const TOAST_ID = "reservation-expired";
const TOAST_DURATION = 60 * 1000;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const cartStore = useCartStore();
const { addToast, removeToast } = useToast();

const isCheckout = computed(() => CHECKOUT_ROUTES.includes(route.name as string));
const hasTickets = computed(() => !!cartStore.items?.some((item: { amount?: number }) => item.amount));

// Set while one of the buttons is working, so the modal stays up through the store's loading state
// and a failed reserve lands back on it rather than on an empty checkout.
const busy = ref(false);

// Also true on arrival at the checkout with a reservation that already ran out. `loading` covers
// the store recreating a reservation, which clears the timeout on the way.
const modalOpen = computed(
  () => isCheckout.value && hasTickets.value && !cartStore.timeLeft && (!cartStore.loading || busy.value)
);

const reserveAgain = async () => {
  busy.value = true;
  try {
    cartStore.reservationId = undefined;
    await cartStore.updateReservation("reserve-again");
  } catch {
    // The store keeps the error, which CartErrorModal shows; the tickets stay expired, so the
    // modal or toast stays the way out.
  } finally {
    busy.value = false;
  }
};

// Leaves first and only then releases the tickets: the checkout may ask to confirm leaving, and
// cancelling that should keep the cart as it was.
const goHome = async () => {
  if (brandconfig.BRAND === "priority") {
    cartStore.emptyCart();
    window.location.href = brandconfig.WEBSITE;
    return;
  }

  busy.value = true;
  try {
    const failure = await router.push("/");
    if (isNavigationFailure(failure)) return;
    await cartStore.deleteReservation().catch(console.error);
    cartStore.emptyCart();
  } finally {
    busy.value = false;
  }
};

const toastReserveAgain = async () => {
  removeToast(TOAST_ID);
  await reserveAgain();
  // The drawer is B2B only; there it shows the reservation that was just made.
  if (cartStore.timeLeft && !isB2C) cartStore.openCart();
};

// Only on the moment the reservation runs out, not on a page load that finds it already gone.
// Skipped while the store is working (recreating a reservation) and once the cart is empty (the last
// ticket removed, or the cart emptied), since neither is an expiry.
watch(
  () => !!cartStore.timeLeft,
  (reserved, wasReserved) => {
    if (reserved) {
      removeToast(TOAST_ID);
      return;
    }
    if (!wasReserved || cartStore.loading || !hasTickets.value || isCheckout.value) return;

    addToast({
      id: TOAST_ID,
      title: t("The reservation time has expired"),
      content: h("div", { class: "flex flex-col items-start gap-2" }, [
        h("span", t("Your tickets are no longer reserved.")),
        h(Button, { class: "min-h-11", onClick: toastReserveAgain }, () => t("Reserve again for 10 minutes")),
      ]),
      status: "warning",
      duration: TOAST_DURATION,
    });
  }
);

// The modal takes over in the checkout, so a toast still up from another page goes.
watch(isCheckout, (checkout) => checkout && removeToast(TOAST_ID));
</script>
